import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient, { setAccessToken, clearTokens } from '@/api/client'
import type { User, Wallet, AuthResponse, MeResponse } from '@/types/user'
import { formatCurrency } from '@/utils/formatCurrency'
import { setLocale, SUPPORTED_LOCALES, type SupportedLocale } from '@/i18n'

export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(null)
  const wallet = ref<Wallet | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  let initPromise: Promise<void> | null = null

  // Getters
  const isAuthenticated = computed(() => user.value !== null)
  const isEmailVerified = computed(() => user.value?.email_verified_at !== null && user.value?.email_verified_at !== undefined)
  const username = computed(() => user.value?.username ?? '')
  const balance = computed(() => wallet.value?.balance ?? 0)
  const currency = computed(() => wallet.value?.currency ?? 'USD')

  // Backward-compatible getters for PlayerTopNav
  const initials = computed(() => {
    const name = user.value?.username
    if (!name) return ''
    return name.slice(0, 2).toUpperCase()
  })
  const displayBalance = computed(() => {
    return formatCurrency(Math.round(balance.value * 100), currency.value)
  })
  const notificationCount = ref(0)

  function setAuthData(data: AuthResponse) {
    user.value = data.user
    setAccessToken(data.access_token)
  }

  async function login(email: string, password: string, twoFactorCode?: string): Promise<{ requires_2fa?: boolean }> {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiClient.post('/api/auth/login', {
        email,
        password,
        two_factor_code: twoFactorCode,
      })

      if (response.data.requires_2fa) {
        return { requires_2fa: true }
      }

      setAuthData(response.data as AuthResponse)
      await fetchProfile()
      return {}
    } catch (e: unknown) {
      const axiosError = e as { response?: { data?: { message?: string }; status?: number } }
      error.value = axiosError.response?.data?.message ?? 'Login failed'
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function register(data: {
    username: string
    email: string
    password: string
    password_confirmation: string
    date_of_birth: string
    country: string
    preferred_currency?: string
    referral_code?: string
  }): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiClient.post('/api/auth/register', data)
      setAuthData(response.data as AuthResponse)
      await fetchProfile()
    } catch (e: unknown) {
      const axiosError = e as { response?: { data?: { message?: string }; status?: number } }
      error.value = axiosError.response?.data?.message ?? 'Registration failed'
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function logout(): Promise<void> {
    try {
      await apiClient.post('/api/auth/logout')
    } catch {
      // Ignore logout errors — clear state regardless
    } finally {
      user.value = null
      wallet.value = null
      error.value = null
      clearTokens()
    }
  }

  async function fetchProfile(): Promise<void> {
    try {
      const response = await apiClient.get<MeResponse>('/api/auth/me')
      user.value = response.data.user
      // API returns balance as decimal strings — parse to numbers at boundary
      const w = response.data.wallet
      wallet.value = w
        ? { balance: Number(w.balance), bonus_balance: Number(w.bonus_balance), currency: w.currency }
        : null
      // Apply user's saved language preference (profile takes priority over localStorage)
      const lang = response.data.user.language
      if (lang && (SUPPORTED_LOCALES as readonly string[]).includes(lang)) {
        await setLocale(lang as SupportedLocale)
      }
    } catch (e: unknown) {
      const axiosError = e as { response?: { status?: number } }
      if (axiosError.response?.status === 401) {
        user.value = null
        wallet.value = null
        clearTokens()
      }
    }
  }

  async function initialize(): Promise<void> {
    // No stored token — nothing to restore
    if (!localStorage.getItem('access_token')) {
      return
    }

    isLoading.value = true
    try {
      // Use stored token to fetch profile; the response interceptor
      // handles transparent refresh if the token has expired.
      await fetchProfile()
    } catch {
      // Token invalid or expired beyond refresh — clear session
      user.value = null
      wallet.value = null
      clearTokens()
    } finally {
      isLoading.value = false
    }
  }

  /** Wait for initialization to complete (used by router guards). */
  function waitForInit(): Promise<void> {
    if (!initPromise) {
      initPromise = initialize()
    }
    return initPromise
  }

  return {
    // State
    user,
    wallet,
    isLoading,
    error,
    notificationCount,
    // Getters
    isAuthenticated,
    isEmailVerified,
    username,
    initials,
    balance,
    currency,
    displayBalance,
    // Actions
    login,
    register,
    logout,
    fetchProfile,
    initialize,
    waitForInit,
  }
})
